import { useEffect } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  removeExternalEventDrawer,
  setExternalEventDrawer,
  setDisableNavigation,
} from 'store/navigationSlice';
import { setPartnerInformation } from 'features/bootstrap/partner';
import { setUser } from 'store/userSlice';
import { swipeclockUserRequest } from 'store/swipeclockUserSlice';
import { navigationRequests, partnerNavigationRequests } from 'lib/router';
import { hasValue } from 'swipeclock-frontend';
import * as shuttle from 'shuttle';
import { selectNavigationAndApp } from 'store/appSlice';

// ################################################
// ### Partner Shuttle Subscriptions
// ################################################

export function usePartnerShuttleSubscriptions() {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const store = useStore();

  const { hasCompanyDrawer, switchRouteUrl } = useSelector(
    selectNavigationAndApp
  );

  useShuttleSubscribe(
    'showDrawer',
    (event) => {
      // This check prevents a navigation issue where changing a child page within 'companies'
      // triggers a navigation to 'companies/company-dashboard' due to the 'showDrawer' call in 'company.js' in Master-Data-UI.
      // By verifying if the company drawer already exists, we avoid this unintended navigation.
      // This also resolves a previous issue where "company-dashboard" was erroneously appended to the end of the URL by the old-shell UI.
      dispatch(setExternalEventDrawer(event));

      if (!hasCompanyDrawer) {
        navigate('/companies/company-dashboard');
      }
    },
    [hasCompanyDrawer]
  );

  useShuttleSubscribe('hideDrawer', () => {
    dispatch(removeExternalEventDrawer());
    navigate('/companies');
  });

  useShuttleSubscribe('onBulkUploadFinished', () => {
    if (pathname.startsWith('/people')) {
      navigate('/people/employees-list');
    } else {
      navigate('/companies/company-employees');
    }
  });

  useShuttleSubscribe('switchLogin', (event) => {
    const { site: siteId, source, token, user } = event ?? {};
    if (source === 'migration') {
      const newUser = { ...user, site: siteId, token, impersonating: false };
      dispatch(setUser(newUser));
    }
  });

  useShuttleSubscribe('refreshDrawer', async () => {
    // The user hasn't changed - the loader initialized the users previously
    // If the users don't exist, we have bigger problems...
    const swipeclockUser = store.getState().swipeclockUser;
    const user = store.getState().user;
    await setPartnerInformation(dispatch, swipeclockUser, user, true);
  });

  // Note: 'switchRouteTo' and 'onNavigateRequested' perform similar actions.
  // Consider refactoring this in the future to use only one of them in 'master-data-ui'.
  // The handling of 'partnerNavigationRequests' and 'navigationRequests' will need to be resolved accordingly.
  useShuttleSubscribe('switchRouteTo', (event) => {
    const { to } = event ?? {};
    if (to) {
      navigate(to);
    }
  });

  useShuttleSubscribe('onNavigateRequested', (event) => {
    const url = partnerNavigationRequests[event?.feature];
    if (hasValue(url)) {
      navigate(url);
    }
  });

  useShuttleSubscribe(
    'onShuttleRouteSwitch',
    (event) => {
      if (hasValue(switchRouteUrl) && event.componentLoaded) {
        switchRouteTo({ to: switchRouteUrl });
      }
    },
    [switchRouteUrl]
  );
}

// ################################################
// ### User Shuttle Subscriptions
// ################################################

export function useUserShuttleSubscriptions() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useShuttleSubscribe('onNavigateRequested', (event) => {
    const url = navigationRequests[event?.feature];
    if (hasValue(url)) {
      navigate(url);
    }
  });

  useShuttleSubscribe('onSwipeclockUserRequested', (event) => {
    dispatch(swipeclockUserRequest(event));
  });

  useShuttleSubscribe('acknowledge', (event) => {
    try {
      shuttle.sendToChild(getContentIframe(), 'acknowledgeReceived', event);
    } catch (error) {
      console.error('Error in acknowledge callback:', error);
    }
  });

  useShuttleSubscribe('paymentSuccess', () => {
    dispatch(
      setDisableNavigation({
        disabledTopAppBar: false,
        hideNavigationDrawer: false,
      })
    );
    navigate('/admin/billing');
  });
}

// ################################################
// ### Helper functions for sending events
// ################################################

export async function switchRouteTo({ to }) {
  try {
    await shuttle.sendToChild(getContentIframe(), 'switchRouteTo', { to });
  } catch (error) {
    console.error('Error switching route:', error);
  }
}

export async function sendSwipeclockUserRequest(payload) {
  try {
    await shuttle.sendToChild(
      getContentIframe(),
      'onSwipeclockUserRecieved',
      payload
    );
  } catch (error) {
    console.error('Error sending swipeclock user request:', error);
  }
}

// ################################################
// ### Helper functions for subscribing
// ################################################

export function useShuttleSubscribe(eventType, callback, dependencies = []) {
  useEffect(() => {
    // Returns unsubscribe function.
    const unsubscribe = shuttle.subscribe(eventType, callback);
    return () => {
      unsubscribe();
    };
  }, dependencies);
}

// ################################################
// ### Private method
// ################################################

function getContentIframe() {
  const iframe = document.getElementById('contentFrame');
  if (!iframe?.contentWindow) {
    throw new Error('Content iframe not available');
  }
  return iframe.contentWindow;
}
